/**
 * OpenFileButton: opens an SOP file in a new tab (plan docs/plans/lms-plan.md
 * 11.1 "opens the effective file (presigned, inline)", 11.3, D15: the file is
 * opened in a new tab, never embedded; 2.3a: downloads are presigned URLs,
 * never proxied bytes; 18.27).
 *
 * The technique is DoneStage.tsx's (openPendingTab, plan 18.27): the tab is
 * opened SYNCHRONOUSLY on the click, inside the click's user activation, with
 * its opener severed at once, and pointed at the presigned URL when the
 * answer arrives. When the browser blocks the tab, a visible link to the URL
 * is shown (target _blank, rel noopener noreferrer); when the request fails,
 * the tab is closed and the error is shown.
 *
 * Props:
 *   resolve   () => Promise<{ url }>   asks the server for the presigned URL (called once per click);
 *                                      an OpenFileError it throws is shown in its own words
 *   label     string                   the button's words
 *   variant?  'outline' | 'primary' | 'link'
 *   testId?   string                   on the button; the fallback link is `${testId}-link`, the error `${testId}-error`
 *   onOpened? () => void               called once the server minted the URL (the tab was pointed at it, or the
 *                                      blocked-tab link shows it); the SOP reading stage gates "I have opened and
 *                                      read" on it (docs/plans/sop-import-plan.md 4.4 item 3)
 *   onFailed? (err) => void            called with the error when the open failed (after the tab is closed)
 */
import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import { openPendingTab } from '../../components/player/DoneStage';
import { linkBtnCls } from '../../components/pickers/shared';

/** A refusal the page words itself (for example "no file to open yet"); shown as it is. */
export class OpenFileError extends Error {}

export interface OpenFileButtonProps {
  resolve: () => Promise<{ url: string }>;
  label: string;
  variant?: 'outline' | 'primary' | 'link';
  testId?: string;
  /** The message shown when the server refuses (else the server's own message). */
  failureMessage?: string;
  disabled?: boolean;
  /** An accessible name that says which file (the visible label is often just "Open"). */
  ariaLabel?: string;
  /** Called after a successful resolve (the server minted, and audited, the file's URL). */
  onOpened?: () => void;
  /** Called with the error of a failed open. */
  onFailed?: (err: unknown) => void;
}

export function OpenFileButton({
  resolve,
  label,
  variant = 'outline',
  testId,
  failureMessage = 'The file could not be opened. Try again.',
  disabled = false,
  ariaLabel,
  onOpened,
  onFailed,
}: OpenFileButtonProps) {
  const [busy, setBusy] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    if (busy || disabled) return;
    // Before any await: the tab is opened while the click's user activation lasts (18.27).
    const tab = openPendingTab();
    setBusy(true);
    setBlockedUrl(null);
    setError(null);
    try {
      const { url } = await resolve();
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlockedUrl(url);
      if (onOpened) onOpened();
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      setError(err instanceof OpenFileError ? err.message : apiErrorMessage(err, failureMessage));
      if (onFailed) onFailed(err);
    } finally {
      setBusy(false);
    }
  };

  const cls = variant === 'primary' ? primaryBtnCls : variant === 'link' ? linkBtnCls : outlineBtnCls();

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        className={cls}
        onClick={open}
        disabled={busy || disabled}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ExternalLink className="h-4 w-4" aria-hidden="true" />}
        {label}
      </button>
      {blockedUrl && (
        <a
          href={blockedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-primary-700 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
          data-testid={testId ? `${testId}-link` : undefined}
        >
          Your browser blocked the new tab: open the file here
        </a>
      )}
      {error && (
        <span role="alert" className="text-xs text-red-600 dark:text-red-400" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </span>
      )}
    </span>
  );
}

export default OpenFileButton;
